import { useState, useEffect } from 'react';
import { Users, BarChart3, TableOfContents } from 'lucide-react';
import { OtherUserBet, getOtherUsersBets, getMatchScoreStatistics, getMatchWinnerStatistics, getGroupStandings } from '../api/matchesApi';
import { GroupStanding } from '../api/groupsApi';
import Table, { TableColumn } from './Table';

interface MatchDetailsTabsProps {
  matchId: string;
  isGroupMatch: boolean;
  groupId?: number | null;
  homeTeamId?: number | null;
  awayTeamId?: number | null;
}

type TabType = 'bets' | 'statistics' | 'standings';

interface ScoreStat {
  score: string;
  count: number;
}

interface WinnerStat {
  winner: number | null;
  count: number;
}

const MatchDetailsTabs: React.FC<MatchDetailsTabsProps> = ({ 
  matchId, 
  isGroupMatch, 
  groupId, 
  homeTeamId, 
  awayTeamId 
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('statistics');
  const [otherUsersBets, setOtherUsersBets] = useState<OtherUserBet[]>([]);
  const [scoreStats, setScoreStats] = useState<ScoreStat[]>([]);
  const [winnerStats, setWinnerStats] = useState<WinnerStat[]>([]);
  const [groupStandings, setGroupStandings] = useState<GroupStanding[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scoreFilter, setScoreFilter] = useState<string>('');

  const isKnockout = !isGroupMatch;

  useEffect(() => {
    if (activeTab === 'bets') {
      loadOtherBets();
    } else if (activeTab === 'statistics') {
      loadStatistics();
    } else if (activeTab === 'standings' && isGroupMatch && groupId) {
      loadGroupStandings();
    }
  }, [activeTab, matchId, isGroupMatch, groupId]);

  const loadOtherBets = async () => {
    try {
      setLoading(true);
      const bets = await getOtherUsersBets(matchId);
      setOtherUsersBets(bets);
    } catch (error) {
      console.error('Error loading other bets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const [scores, winners] = await Promise.all([
        getMatchScoreStatistics(matchId),
        getMatchWinnerStatistics(matchId)
      ]);
      setScoreStats(scores);
      setWinnerStats(winners);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupStandings = async () => {
    if (!groupId) return;
    try {
      setLoading(true);
      const standings = await getGroupStandings(groupId);
      setGroupStandings(standings);
    } catch (error) {
      console.error('Error loading group standings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBetResultBadge = (bet: OtherUserBet) => {
    return (
      <div className="flex items-center">
        <div className={`w-2 h-2 rounded-full ml-2 ${bet.score_success ? "bg-green-500" : "bg-red-500"}`} />
        <div className={`w-2 h-2 rounded-full ${bet.result_success ? "bg-green-500" : "bg-red-500"}`} />
      </div>
    );
  };

  // Filter bets based on search term and score filter
  const filteredBets = otherUsersBets.filter(bet => {
    const matchesSearch = searchTerm === '' || 
      bet.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesScore = scoreFilter === '' || 
      `${bet.away_score}-${bet.home_score}` === scoreFilter;
    
    return matchesSearch && matchesScore;
  });

  // Get unique score combinations for the filter dropdown
  const uniqueScores = Array.from(new Set(otherUsersBets.map(bet => `${bet.away_score}-${bet.home_score}`))).sort();

  // Define standings table columns (same as GroupBets page)
  const standingsColumns: TableColumn<GroupStanding>[] = [
    { key: 'team_logo', title: '', render: (item) => <img src={item.team_logo} alt={item.team_name} className="h-6 w-6 rounded-full" />, width: '32px' },
    { key: 'team_name', title: 'קבוצה', render: (item) => item.team_name, width: '50px' },
    { key: 'played', title: 'מש', render: (item) => item.played, width: '24px' },
    { key: 'won', title: 'נ', render: (item) => item.won, width: '24px' },
    { key: 'drawn', title: 'ת', render: (item) => item.drawn, width: '24px' },
    { key: 'lost', title: 'ה', render: (item) => item.lost, width: '24px' },
    // { key: 'goals_for', title: 'ז', render: (item) => item.goals_for, width: '24px' },
    // { key: 'goals_against', title: 'נ', render: (item) => item.goals_against, width: '24px' },
    { key: 'goal_difference', title: '+/-', render: (item) => item.goal_difference, width: '24px' },
    { key: 'points', title: 'נק', render: (item) => <span className="font-bold text-green-400">{item.points}</span>, width: '32px' },
  ];

  return (
    <div className="border-t border-gray-800">
      {/* Tab Headers */}
      <div className="flex border-b border-gray-800">
        <button
          onClick={() => setActiveTab('bets')}
          className={`flex-1 flex items-center justify-center py-3 px-4 transition-colors ${
            activeTab === 'bets' 
              ? 'text-blue-400 border-b-2 border-blue-400' 
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <Users className="w-4 h-4 ml-2" />
          <span className="text-sm font-medium"></span>
        </button>
        
        <button
          onClick={() => setActiveTab('statistics')}
          className={`flex-1 flex items-center justify-center py-3 px-4 transition-colors ${
            activeTab === 'statistics' 
              ? 'text-blue-400 border-b-2 border-blue-400' 
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          <BarChart3 className="w-4 h-4 ml-2" />
          <span className="text-sm font-medium"></span>
        </button>
        
        {isGroupMatch && (
          <button
            onClick={() => setActiveTab('standings')}
            className={`flex-1 flex items-center justify-center py-3 px-4 transition-colors ${
              activeTab === 'standings' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <TableOfContents className="w-4 h-4 ml-2" />
            <span className="text-sm font-medium"></span>
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="px-4 pb-4 pt-4 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="text-gray-400 text-center py-4">טוען...</div>
        ) : (
          <>
            {activeTab === 'bets' && (
              <div className="space-y-2">
                {/* Search and Filter Controls */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  {/* Username Search */}
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="חיפוש לפי שם משתמש..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Score Filter */}
                  <div className="sm:w-32">
                    <select
                      value={scoreFilter}
                      onChange={(e) => setScoreFilter(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">כל התוצאות</option>
                      {uniqueScores.map(score => (
                        <option key={score} value={score}>{score}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Results Count */}
                <div className="text-xs text-gray-400 mb-2 text-center">
                  מציג {filteredBets.length} מתוך {otherUsersBets.length} משתמשים
                </div>

                {/* Bets List */}
                {filteredBets.map((bet, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3"
                  >
                    <div className="flex items-center">
                      <span className="text-sm font-medium">{bet.username}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center bg-gray-700 px-2 py-1 rounded text-sm ml-2">
                        <span className={bet.home_score === bet.away_score && bet.winner_team_id === homeTeamId ? 'underline decoration-gray-400 decoration-2 underline-offset-4' : undefined}>{bet.home_score}</span>
                        <span className="mx-1">-</span>
                        <span className={bet.home_score === bet.away_score && bet.winner_team_id === awayTeamId ? 'underline decoration-gray-400 decoration-2 underline-offset-4' : undefined}>{bet.away_score}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-400 ml-1">נק׳:</span>
                        <span className={`text-sm font-bold ${
                          bet.points === 0 
                            ? 'text-gray-400' 
                            : bet.score_success && bet.result_success
                              ? 'text-green-600'
                              : 'text-blue-600'
                        }`}>
                          {bet.points}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'statistics' && (
              <div className="space-y-6">
                {/* Winner Statistics */}
                <div>
                  <h3 className="text-center text-sm font-medium mb-4 text-white">מנצחת</h3>
                  <div className="flex items-end justify-center gap-4" style={{height: '120px'}}>
                    {/* Home Team (Right) */}
                    <div className="flex flex-col items-center h-full justify-end">
                      <span className="text-sm text-white mb-2">
                        {winnerStats.find(s => s.winner === homeTeamId)?.count || 0}
                      </span>
                      <div 
                        className={`w-16 ${winnerStats.find(s => s.winner === homeTeamId)?.count ? 'bg-blue-600' : 'bg-gray-400'}`}
                        style={{
                          height: `${Math.max(
                            (winnerStats.find(s => s.winner === homeTeamId)?.count || 0) / 
                            (winnerStats.reduce((sum, s) => sum + s.count, 0)) * 100,
                            5
                          )}%`
                        }}
                      ></div>
                    </div>
                    
                    {/* Draw (Middle) - Only for group stage */}
                    {!isKnockout && (
                      <div className="flex flex-col items-center h-full justify-end">
                        <span className="text-sm text-white mb-2">
                          {winnerStats.find(s => s.winner === null)?.count || 0}
                        </span>
                        <div 
                          className={`w-16 ${winnerStats.find(s => s.winner === null)?.count ? 'bg-gray-500' : 'bg-gray-400'}`}
                          style={{
                            height: `${Math.max(
                              (winnerStats.find(s => s.winner === null)?.count || 0) / 
                              (winnerStats.reduce((sum, s) => sum + s.count, 0)) * 100,
                              5
                            )}%`
                          }}
                        ></div>
                      </div>
                    )}
                    
                    {/* Away Team (Left) */}
                    <div className="flex flex-col items-center h-full justify-end">
                      <span className="text-sm text-white mb-2">
                        {winnerStats.find(s => s.winner === awayTeamId)?.count || 0}
                      </span>
                      <div 
                        className={`w-16 ${winnerStats.find(s => s.winner === awayTeamId)?.count ? 'bg-green-500' : 'bg-gray-400'}`}
                        style={{
                          height: `${Math.max(
                            (winnerStats.find(s => s.winner === awayTeamId)?.count || 0) / 
                            (winnerStats.reduce((sum, s) => sum + s.count, 0)) * 100,
                            5
                          )}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Score Statistics */}
                <div>
                  <h3 className="text-center text-sm font-medium mb-4 text-white">תוצאות</h3>
                  <div className="space-y-2">
                    {scoreStats.map((stat, index) => {
                      const maxCount = Math.max(...scoreStats.map(s => s.count));
                      const widthPercent = maxCount > 0 ? (stat.count / maxCount) * 100 : 0;
                      
                      return (
                        <div key={stat.score} className="flex items-center justify-center">
                            <span className="text-sm text-white w-12 text-right ml-2">{stat.score}</span>
                            <div className="flex" style={{ width: "100%", maxWidth: "256px" }}>
                                <div 
                                className="h-8" 
                                style={{
                                    width: `${widthPercent}%`,
                                    backgroundColor: `hsl(${120}, 70%, ${60 - index * 4}%)`
                                }}
                                ></div>
                            </div>
                            <span className="text-sm text-white w-8 text-left mr-2">{stat.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'standings' && isGroupMatch && (
              <div className="mt-4">
                <Table
                  data={groupStandings}
                  columns={standingsColumns}
                  keyExtractor={(item) => item.team_id.toString()}
                  qualifyingPositions={2}
                  emptyMessage="אין נתוני דירוג זמינים"
                  isLoading={loading}
                  compact={true}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MatchDetailsTabs; 